import { userApi } from '@/api/generatedApi';
import { getConfig } from '@/config/auth.config.ts';
import { HTTP_STATUS_CODE_MULTIPLE_CHOICES, HTTP_STATUS_CODE_OK } from '@/constants';
import { UserRole, type UserRole as UserRoleType } from '@/generated';
import { RouteNames } from '@/router/routeNames';
import { consumeRedirectAfterLogin, getRedirectAfterLogin } from '@/utils/localStorageUtil';
import type { AccountInfo, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

const accountMatchesType = () => (account: AccountInfo) =>
    account.tenantId === getConfig().tenantId;

const isInteractionInProgressError = (error: unknown): boolean =>
    typeof error === 'object' &&
    error !== null &&
    'errorCode' in error &&
    (error as { errorCode?: string }).errorCode === 'interaction_in_progress';

const clearStaleInteractionStatus = (clientId: string): void => {
    const possibleKeys = [`msal.${clientId}.interaction.status`, 'msal.interaction.status'];

    possibleKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });
};

type CurrentUserResponse = {
    roles?: unknown;
    userRoles?: unknown;
    role?: unknown;
};

const isUserRole = (value: unknown): value is UserRoleType =>
    Object.values(UserRole).includes(value as UserRoleType);

const getRoleValues = (roleValue: unknown): unknown[] => {
    if (Array.isArray(roleValue)) {
        return roleValue;
    }

    if (typeof roleValue === 'string') {
        return roleValue.split(',').map(role => role.trim());
    }

    return [roleValue];
};

const getUserRoles = (data: unknown): UserRoleType[] => {
    const currentUser = (data ?? {}) as CurrentUserResponse;
    const roleValue = currentUser.roles ?? currentUser.userRoles ?? currentUser.role ?? data;
    const roleValues = getRoleValues(roleValue);

    return roleValues.filter(isUserRole);
};

const useAuthStore = defineStore('auth', () => {
    const isAuthenticated = ref<boolean>(false);
    const isLoginInProgress = ref<boolean>(false);
    const userRoles = ref<UserRoleType[]>([]);
    const hasLoadedUserRole = ref<boolean>(false);
    const publicClientApplication = ref<IPublicClientApplication | undefined>(undefined);
    const router = useRouter();

    const getOrCreateMsal = async (): Promise<IPublicClientApplication> => {
        if (!publicClientApplication.value) {
            const config = getConfig();
            const msal = new PublicClientApplication(config.msalConfig);
            await msal.initialize();
            const redirectResult = await msal.handleRedirectPromise();
            if (redirectResult?.account) {
                msal.setActiveAccount(redirectResult.account);
                isAuthenticated.value = true;
                consumeRedirectAfterLogin();
            }
            publicClientApplication.value = msal;
        }

        return publicClientApplication.value;
    };

    const trySilentLogin = async (): Promise<AccountInfo | undefined> => {
        try {
            const msal = await getOrCreateMsal();

            const accounts = msal.getAllAccounts();
            const account = accounts.find(accountMatchesType());

            if (account) {
                msal.setActiveAccount(account);
                isAuthenticated.value = true;

                return account;
            } else {
                isAuthenticated.value = false;
                userRoles.value = [];
                hasLoadedUserRole.value = false;
                return undefined;
            }
        } catch (e) {
            console.warn('Silent login failed', e);
            isAuthenticated.value = false;
            userRoles.value = [];
            hasLoadedUserRole.value = false;
            return undefined;
        }
    };

    const trySilentLogins = async () => {
        const account: AccountInfo | undefined = await trySilentLogin();

        if (account) {
            return account;
        }

        return trySilentLogin();
    };

    const explicitLogin = async (): Promise<void> => {
        if (isLoginInProgress.value) {
            return;
        }

        const config = getConfig();
        const msal = await getOrCreateMsal();

        try {
            isLoginInProgress.value = true;
            const request: RedirectRequest = {
                redirectStartPage: getRedirectAfterLogin() ?? '/',
                scopes: config.scopes,
            };
            try {
                await msal.loginRedirect(request);
                return;
            } catch (error: unknown) {
                if (!isInteractionInProgressError(error)) {
                    console.error(error);
                    return;
                }
            }

            await msal.handleRedirectPromise();
            clearStaleInteractionStatus(config.msalConfig.auth.clientId);

            try {
                await msal.loginRedirect(request);
            } catch (retryError: unknown) {
                if (isInteractionInProgressError(retryError)) {
                    console.warn(
                        'Login blocked by active interaction. Retry after redirect handling.'
                    );
                    return;
                }

                console.error(retryError);
            }
        } finally {
            isLoginInProgress.value = false;
        }
    };

    const logout = async () => {
        isAuthenticated.value = false;
        userRoles.value = [];
        hasLoadedUserRole.value = false;
        router.push({ name: RouteNames.Index });
        if (!publicClientApplication.value) {
            return;
        }
        await publicClientApplication.value.handleRedirectPromise();
        await publicClientApplication.value.logoutRedirect({ postLogoutRedirectUri: '/' });
    };

    const getToken = async (retry = false): Promise<string> => {
        try {
            if (publicClientApplication.value) {
                const res = await publicClientApplication.value.acquireTokenSilent({
                    scopes: getConfig()?.scopes ?? [],
                });
                isAuthenticated.value = !!res?.accessToken;
                return res?.accessToken || '';
            } else {
                await trySilentLogins();
                if (publicClientApplication.value && !retry) {
                    return getToken(true);
                }
            }
        } catch {
            await trySilentLogins();
            if (publicClientApplication.value && !retry) {
                return getToken(true);
            }
        }
        return '';
    };

    const getAccountInfo = (): AccountInfo | undefined =>
        publicClientApplication.value?.getActiveAccount() || undefined;

    const loadUserRole = async (): Promise<UserRoleType[]> => {
        if (hasLoadedUserRole.value) {
            return userRoles.value;
        }

        try {
            const response = await userApi.apiV1UserWhoAmIGet();
            userRoles.value =
                response.status >= HTTP_STATUS_CODE_OK &&
                response.status < HTTP_STATUS_CODE_MULTIPLE_CHOICES
                    ? getUserRoles(response.data)
                    : [];
        } catch (error) {
            console.error('Could not load user role', error);
            userRoles.value = [];
        } finally {
            hasLoadedUserRole.value = true;
        }

        return userRoles.value;
    };

    const user = computed<AccountInfo | undefined>(() => getAccountInfo());
    const isAdmin = computed(() => userRoles.value.includes(UserRole.Admin));

    return {
        logout,
        getToken,
        explicitLogin,
        trySilentLogins,
        getAccountInfo,
        loadUserRole,
        isAuthenticated,
        isAdmin,
        user,
    };
});

export default useAuthStore;
